import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";

import { ErrorBoundary } from "@/components/layout";
import { Layout } from "@/layouts";
import { RouteSuspense } from "@/components/loading";
import { ROUTES } from "@/constants/routes";
import { useScrollToTop, useRoutePreload, usePageTracking } from "@/hooks";
import { features } from "@/config/features";
import { ChatWidget } from "@/components/chat/ChatWidget";
import { createLazyComponent } from "@/utils/lazyImport";

// Lazy load all route components for code splitting with ad blocker handling
const HomePage = createLazyComponent(
  () => import("@/pages/HomePage"),
  undefined,
  "Home Page"
);
const EcosystemPage = createLazyComponent(
  () => import("@/pages/EcosystemPage"),
  undefined,
  "Ecosystem Page"
);
const AIEnginePage = createLazyComponent(
  () => import("@/pages/AIEnginePage"),
  undefined,
  "AI Engine Page"
);
const TokenomicsPage = createLazyComponent(
  () => import("@/pages/TokenomicsPage"),
  undefined,
  "Tokenomics Page"
);
const TractionPage = createLazyComponent(
  () => import("@/pages/TractionPage"),
  undefined,
  "Traction Page"
);
const AboutUsPage = createLazyComponent(
  () => import("@/pages/AboutUsPage"),
  undefined,
  "About Us Page"
);
const ElevateToAlphaPage = createLazyComponent(
  () => import("@/pages/ElevateToAlphaPage"),
  undefined,
  "Elevate to Alpha Page"
);
const OurTeamPage = createLazyComponent(
  () => import("@/pages/OurTeamPage"),
  undefined,
  "Our Team Page"
);
const UserJourneysPage = createLazyComponent(
  () => import("@/pages/UserJourneysPage"),
  undefined,
  "User Journeys Page"
);
const IntegrityGatedAmbassadorChannelPage = createLazyComponent(
  () => import("@/pages/IntegrityGatedAmbassadorChannelPage"),
  undefined,
  "IGAC Page"
);
const RegulatoryCompliancePage = createLazyComponent(
  () => import("@/pages/RegulatoryCompliancePage"),
  undefined,
  "Regulatory Compliance Page"
);
const SocialImpactCollectivePage = createLazyComponent(
  () => import("@/pages/SocialImpactCollectivePage"),
  undefined,
  "Social Impact Collective Page"
);
const RoadmapPage = createLazyComponent(
  () => import("@/pages/RoadmapPage"),
  undefined,
  "Roadmap Page"
);
const PrivacyPolicyPage = createLazyComponent(
  () => import("@/pages/legal/PrivacyPolicyPage"),
  undefined,
  "Privacy Policy Page"
);
const TermsOfServicePage = createLazyComponent(
  () => import("@/pages/legal/TermsOfServicePage"),
  undefined,
  "Terms of Service Page"
);
const DocumentationPage = createLazyComponent(
  () => import("@/pages/DocumentationPage"),
  undefined,
  "Documentation Page"
);
const WhitepaperPage = createLazyComponent(
  () => import("@/pages/WhitepaperPage"),
  undefined,
  "Whitepaper Page"
);
const TokenEconomicsDownloadPage = createLazyComponent(
  () => import("@/pages/TokenEconomicsDownloadPage"),
  undefined,
  "Token Economics Download Page"
);
const ComplianceHubPage = createLazyComponent(
  () => import("@/pages/ComplianceHubPage"),
  undefined,
  "Compliance Hub Page"
);
const SecurityPage = createLazyComponent(
  () => import("@/pages/SecurityPage"),
  undefined,
  "Security Page"
);
const BlogPage = createLazyComponent(
  () => import("@/pages/BlogPage"),
  undefined,
  "Blog Page"
);
const LayerZeroOFTExplainedPage = createLazyComponent(
  () => import("@/pages/blog/LayerZeroOFTExplainedPage"),
  undefined,
  "LayerZero OFT Explained Blog Post"
);
const BeyondTheAuditContinuousSecurityValidationPage = createLazyComponent(
  () => import("@/pages/blog/BeyondTheAuditContinuousSecurityValidationPage"),
  undefined,
  "Beyond the Audit Continuous Security Validation Blog Post"
);
const BridgesAttackSurfacePage = createLazyComponent(
  () => import("@/pages/blog/BridgesAttackSurfacePage"),
  undefined,
  "Bridges Attack Surface Blog Post"
);
const BlockchainAnalyticsInvestorProtectionPage = createLazyComponent(
  () => import("@/pages/blog/BlockchainAnalyticsInvestorProtectionPage"),
  undefined,
  "Blockchain Analytics Investor Protection Blog Post"
);
const BugBountiesResponsibleDisclosurePage = createLazyComponent(
  () => import("@/pages/blog/BugBountiesResponsibleDisclosurePage"),
  undefined,
  "Bug Bounties Responsible Disclosure Blog Post"
);
const ComplianceFirstArchitecturePage = createLazyComponent(
  () => import("@/pages/blog/ComplianceFirstArchitecturePage"),
  undefined,
  "Compliance First Architecture Blog Post"
);
const SecurityByDesignNotSloganPage = createLazyComponent(
  () => import("@/pages/blog/SecurityByDesignNotSloganPage"),
  undefined,
  "Security By Design Not Slogan Blog Post"
);
const OFTvsWrappedTokensPage = createLazyComponent(
  () => import("@/pages/blog/OFTvsWrappedTokensPage"),
  undefined,
  "OFT vs Wrapped Tokens Blog Post"
);
const SupplyIntegrityAcrossChainsPage = createLazyComponent(
  () => import("@/pages/blog/SupplyIntegrityAcrossChainsPage"),
  undefined,
  "Supply Integrity Across Chains Blog Post"
);
const TokenomicsYouCanVerifyPage = createLazyComponent(
  () => import("@/pages/blog/TokenomicsYouCanVerifyPage"),
  undefined,
  "Tokenomics You Can Verify Blog Post"
);
const CrossChainIdentityReputationPage = createLazyComponent(
  () => import("@/pages/blog/CrossChainIdentityReputationPage"),
  undefined,
  "Cross-Chain Identity and Reputation Blog Post"
);
const CrossChainBridgesOFTMessagingPage = createLazyComponent(
  () => import("@/pages/blog/CrossChainBridgesOFTMessagingPage"),
  undefined,
  "Cross-Chain Bridges OFT Messaging Blog Post"
);
const IncidentResponseOmnichainPage = createLazyComponent(
  () => import("@/pages/blog/IncidentResponseOmnichainPage"),
  undefined,
  "Incident Response for Omnichain Systems Blog Post"
);
const InstitutionalGradeSecurityPage = createLazyComponent(
  () => import("@/pages/blog/InstitutionalGradeSecurityPage"),
  undefined,
  "Institutional-Grade Security Blog Post"
);
const BridgeUXSecurityChecklistPage = createLazyComponent(
  () => import("@/pages/blog/BridgeUXSecurityChecklistPage"),
  undefined,
  "Bridge UX Security Checklist Blog Post"
);
const SmartContractAuditsExplainedPage = createLazyComponent(
  () => import("@/pages/blog/SmartContractAuditsExplainedPage"),
  undefined,
  "Smart Contract Audits Explained Blog Post"
);
const MonitoringThreatDetectionPage = createLazyComponent(
  () => import("@/pages/blog/MonitoringThreatDetectionPage"),
  undefined,
  "Monitoring and Threat Detection Blog Post"
);
const SecureKeyManagementPage = createLazyComponent(
  () => import("@/pages/blog/SecureKeyManagementPage"),
  undefined,
  "Secure Key Management Blog Post"
);
const TwoFactorAuthenticationCryptoPage = createLazyComponent(
  () => import("@/pages/blog/TwoFactorAuthenticationCryptoPage"),
  undefined,
  "2FA for Crypto Accounts Blog Post"
);
const EndToEndEncryptionFintechPage = createLazyComponent(
  () => import("@/pages/blog/EndToEndEncryptionFintechPage"),
  undefined,
  "End-to-End Encryption Fintech Blog Post"
);
const EnforcingAccountabilityPostLaunchGovernanceOversightPage = createLazyComponent(
  () => import("@/pages/blog/EnforcingAccountabilityPostLaunchGovernanceOversightPage"),
  undefined,
  "Enforcing Accountability Post Launch Governance Oversight Blog Post"
);
const EmergencyWithdrawalsCircuitBreakersPage = createLazyComponent(
  () => import("@/pages/blog/EmergencyWithdrawalsCircuitBreakersPage"),
  undefined,
  "Emergency Withdrawals and Circuit Breakers Blog Post"
);
const RugPullsLaunchScamsPage = createLazyComponent(
  () => import("@/pages/blog/RugPullsLaunchScamsPage"),
  undefined,
  "Rug Pulls and Launch Scams Blog Post"
);
const AIBlockchainSecurityPage = createLazyComponent(
  () => import("@/pages/blog/AIBlockchainSecurityPage"),
  undefined,
  "AI Blockchain Security Blog Post"
);
const AlphaAIEnginePage = createLazyComponent(
  () => import("@/pages/blog/AlphaAIEnginePage"),
  undefined,
  "Alpha AI Engine Blog Post"
);
const ElevateToAlphaBlogPage = createLazyComponent(
  () => import("@/pages/blog/ElevateToAlphaPage"),
  undefined,
  "Elevate to Alpha Blog Post"
);
const AlphaShowcasesPage = createLazyComponent(
  () => import("@/pages/blog/AlphaShowcasesPage"),
  undefined,
  "Alpha Showcases Blog Post"
);
const AlphaShortlistsPage = createLazyComponent(
  () => import("@/pages/blog/AlphaShortlistsPage"),
  undefined,
  "Alpha Shortlists Blog Post"
);
const ProofOfExecutionPage = createLazyComponent(
  () => import("@/pages/blog/ProofOfExecutionPage"),
  undefined,
  "Proof of Execution Blog Post"
);
const TalentMarketplaceEscrowPage = createLazyComponent(
  () => import("@/pages/blog/TalentMarketplaceEscrowPage"),
  undefined,
  "Talent Marketplace Escrow Blog Post"
);
const LiquidityPlanningToolsPage = createLazyComponent(
  () => import("@/pages/blog/LiquidityPlanningToolsPage"),
  undefined,
  "Liquidity Planning Tools Blog Post"
);
const MarketMakerInventoryUnlocksPage = createLazyComponent(
  () => import("@/pages/blog/MarketMakerInventoryUnlocksPage"),
  undefined,
  "Market Maker Inventory Unlocks Blog Post"
);
const DEXListingReadinessPage = createLazyComponent(
  () => import("@/pages/blog/DEXListingReadinessPage"),
  undefined,
  "DEX Listing Readiness Blog Post"
);
const UtilityBoundTokens101Page = createLazyComponent(
  () => import("@/pages/blog/UtilityBoundTokens101Page"),
  undefined,
  "Utility-Bound Tokens 101 Blog Post"
);
const DisclosureDisciplinePage = createLazyComponent(
  () => import("@/pages/blog/DisclosureDisciplinePage"),
  undefined,
  "Disclosure Discipline Blog Post"
);
const StakingAnalyticsDashboardPage = createLazyComponent(
  () => import("@/pages/blog/StakingAnalyticsDashboardPage"),
  undefined,
  "Staking Analytics Dashboard Blog Post"
);
const MeasuringUtilityPage = createLazyComponent(
  () => import("@/pages/blog/MeasuringUtilityPage"),
  undefined,
  "Measuring Utility Blog Post"
);
const LiquidityControls101Page = createLazyComponent(
  () => import("@/pages/blog/LiquidityControls101Page"),
  undefined,
  "Liquidity Controls 101 Blog Post"
);
const AllocationBandsExplainedPage = createLazyComponent(
  () => import("@/pages/blog/AllocationBandsExplainedPage"),
  undefined,
  "Allocation Bands Explained Blog Post"
);
const TransparentOrderBooksPage = createLazyComponent(
  () => import("@/pages/blog/TransparentOrderBooksPage"),
  undefined,
  "Transparent Order Books Blog Post"
);
const UnlocksCliffsLinearVestingPage = createLazyComponent(
  () => import("@/pages/blog/UnlocksCliffsLinearVestingPage"),
  undefined,
  "Unlocks Cliffs Linear Vesting Blog Post"
);
const ThirtyMonthEmissionsPlanPage = createLazyComponent(
  () => import("@/pages/blog/ThirtyMonthEmissionsPlanPage"),
  undefined,
  "30-Month Emissions Plan Blog Post"
);
const TransparencyStackPage = createLazyComponent(
  () => import("@/pages/blog/TransparencyStackPage"),
  undefined,
  "Transparency Stack Blog Post"
);
const GovernanceApprovedParameterChangesPage = createLazyComponent(
  () => import("@/pages/blog/GovernanceApprovedParameterChangesPage"),
  undefined,
  "Governance-Approved Parameter Changes Blog Post"
);
const ReadinessGatesDisclosuresPage = createLazyComponent(
  () => import("@/pages/blog/ReadinessGatesDisclosuresPage"),
  undefined,
  "Readiness Gates and Disclosures Blog Post"
);
const ValueAccrualEnginePage = createLazyComponent(
  () => import("@/pages/blog/ValueAccrualEnginePage"),
  undefined,
  "Value-Accrual Engine Blog Post"
);
const BuybackBurnVsAllocatePage = createLazyComponent(
  () => import("@/pages/blog/BuybackBurnVsAllocatePage"),
  undefined,
  "Buyback-and-Burn vs Buyback-and-Allocate Blog Post"
);
const SingleSidedVsLPStakingPage = createLazyComponent(
  () => import("@/pages/blog/SingleSidedVsLPStakingPage"),
  undefined,
  "Single-Sided vs LP Staking Blog Post"
);
const NonCashableRewardsPage = createLazyComponent(
  () => import("@/pages/blog/NonCashableRewardsPage"),
  undefined,
  "Non-Cashable Rewards Blog Post"
);
const AMLCTFComplianceSafetyPage = createLazyComponent(
  () => import("@/pages/blog/AMLCTFComplianceSafetyPage"),
  undefined,
  "AML/CTF Compliance Safety Blog Post"
);
const GovernanceRisksOmnichainPage = createLazyComponent(
  () => import("@/pages/blog/GovernanceRisksOmnichainPage"),
  undefined,
  "Governance Risks Omnichain Blog Post"
);
const GuardiansOfCrossChainIntegrityPage = createLazyComponent(
  () => import("@/pages/blog/GuardiansOfCrossChainIntegrityPage"),
  undefined,
  "Guardians of Cross-Chain Integrity Blog Post"
);
const FastBridgesSystemicRiskPage = createLazyComponent(
  () => import("@/pages/blog/FastBridgesSystemicRiskPage"),
  undefined,
  "Fast Bridges Systemic Risk Blog Post"
);
const FromWallStreetToWeb3AdaptingTraditionalRiskControlsPage = createLazyComponent(
  () => import("@/pages/blog/FromWallStreetToWeb3AdaptingTraditionalRiskControlsPage"),
  undefined,
  "From Wall Street to Web3 Adapting Traditional Risk Controls Blog Post"
);
const ReplayAttacksReorgRiskPage = createLazyComponent(
  () => import("@/pages/blog/ReplayAttacksReorgRiskPage"),
  undefined,
  "Replay Attacks Reorg Risk Blog Post"
);
const ResilienceAsAServicePage = createLazyComponent(
  () => import("@/pages/blog/ResilienceAsAServicePage"),
  undefined,
  "Resilience as a Service Blog Post"
);
const RugPullDetectionPage = createLazyComponent(
  () => import("@/pages/blog/RugPullDetectionPage"),
  undefined,
  "Rug Pull Detection Blog Post"
);
const MEVFrontRunningPage = createLazyComponent(
  () => import("@/pages/blog/MEVFrontRunningPage"),
  undefined,
  "MEV Front Running Blog Post"
);
const CryptoRegulationTrends2026Page = createLazyComponent(
  () => import("@/pages/blog/CryptoRegulationTrends2026Page"),
  undefined,
  "Crypto Regulation Trends 2026 Blog Post"
);
const DynamicComplianceAdaptingToRegulatoryChangeInACrossChainPlatformPage =
  createLazyComponent(
    () =>
      import("@/pages/blog/DynamicComplianceAdaptingToRegulatoryChangeInACrossChainPlatformPage"),
    undefined,
    "Dynamic Compliance Adapting to Regulatory Change in a Cross-Chain Platform Blog Post"
  );
const HowToChooseTrustedLaunchpadPage = createLazyComponent(
  () => import("@/pages/blog/HowToChooseTrustedLaunchpadPage"),
  undefined,
  "How to Choose Trusted Launchpad Blog Post"
);
const UserPrivacyVsCompliancePage = createLazyComponent(
  () => import("@/pages/blog/UserPrivacyVsCompliancePage"),
  undefined,
  "User Privacy vs Compliance Blog Post"
);
const SecurityMetricsEveryPlatformPage = createLazyComponent(
  () => import("@/pages/blog/SecurityMetricsEveryPlatformPage"),
  undefined,
  "Security Metrics Every Platform Blog Post"
);
const SecurityCultureProductFeaturePage = createLazyComponent(
  () => import("@/pages/blog/SecurityCultureProductFeaturePage"),
  undefined,
  "Security Culture Product Feature Blog Post"
);
const SimulatingTheLaunchHowWeTestAndTuneOperationsBeforeGoLivePage = createLazyComponent(
  () =>
    import("@/pages/blog/SimulatingTheLaunchHowWeTestAndTuneOperationsBeforeGoLivePage"),
  undefined,
  "Simulating the Launch How We Test and Tune Operations Before Go-Live Blog Post"
);
const StakingAndSecurityPage = createLazyComponent(
  () => import("@/pages/blog/StakingAndSecurityPage"),
  undefined,
  "Staking and Security Blog Post"
);
const TheTrustStackCombiningCodeComplianceAndCommunityToSecureWeb3InvestmentsPage =
  createLazyComponent(
    () =>
      import("@/pages/blog/TheTrustStackCombiningCodeComplianceAndCommunityToSecureWeb3InvestmentsPage"),
    undefined,
    "The Trust Stack Combining Code Compliance and Community to Secure Web3 Investments Blog Post"
  );
const OnChainMonitoring101Page = createLazyComponent(
  () => import("@/pages/blog/OnChainMonitoring101Page"),
  undefined,
  "On-Chain Monitoring 101 Blog Post"
);
const AdminDashboardAndAnalyticsPage = createLazyComponent(
  () => import("@/pages/blog/AdminDashboardAndAnalyticsPage"),
  undefined,
  "Admin Dashboard and Analytics Blog Post"
);
const AnonymousCrossChainReputationPage = createLazyComponent(
  () => import("@/pages/blog/AnonymousCrossChainReputationPage"),
  undefined,
  "Anonymous Cross Chain Reputation Blog Post"
);
const AnonymousStakingAndGovernancePage = createLazyComponent(
  () => import("@/pages/blog/AnonymousStakingAndGovernancePage"),
  undefined,
  "Anonymous Staking and Governance Blog Post"
);
const CAIPStandardsCrossChainIdentityPage = createLazyComponent(
  () => import("@/pages/blog/CAIPStandardsCrossChainIdentityPage"),
  undefined,
  "CAIP Standards Cross Chain Identity Blog Post"
);
const ComplianceAwareLaunchWorkflowsPage = createLazyComponent(
  () => import("@/pages/blog/ComplianceAwareLaunchWorkflowsPage"),
  undefined,
  "Compliance Aware Launch Workflows Blog Post"
);
const ComprehensiveAuditLoggingPage = createLazyComponent(
  () => import("@/pages/blog/ComprehensiveAuditLoggingPage"),
  undefined,
  "Comprehensive Audit Logging Blog Post"
);
const CrossChainCredentialSystemsPage = createLazyComponent(
  () => import("@/pages/blog/CrossChainCredentialSystemsPage"),
  undefined,
  "Cross Chain Credential Systems Blog Post"
);
const CrossChainVestingPage = createLazyComponent(
  () => import("@/pages/blog/CrossChainVestingPage"),
  undefined,
  "Cross Chain Vesting Blog Post"
);
const GovernanceParticipationWithoutIdentityPage = createLazyComponent(
  () => import("@/pages/blog/GovernanceParticipationWithoutIdentityPage"),
  undefined,
  "Governance Participation Without Identity Blog Post"
);
const InfluencerCreatorPlatformPage = createLazyComponent(
  () => import("@/pages/blog/InfluencerCreatorPlatformPage"),
  undefined,
  "Influencer Creator Platform Blog Post"
);
const LaunchpadMilestoneManagementPage = createLazyComponent(
  () => import("@/pages/blog/LaunchpadMilestoneManagementPage"),
  undefined,
  "Launchpad Milestone Management Blog Post"
);
const LegalComplianceFrameworksAnonymousWeb3Page = createLazyComponent(
  () => import("@/pages/blog/LegalComplianceFrameworksAnonymousWeb3Page"),
  undefined,
  "Legal Compliance Frameworks Anonymous Web3 Blog Post"
);
const MultiSiteEcosystemArchitecturePage = createLazyComponent(
  () => import("@/pages/blog/MultiSiteEcosystemArchitecturePage"),
  undefined,
  "Multi Site Ecosystem Architecture Blog Post"
);
const NonCustodialMarketplaceArchitecturePage = createLazyComponent(
  () => import("@/pages/blog/NonCustodialMarketplaceArchitecturePage"),
  undefined,
  "Non Custodial Marketplace Architecture Blog Post"
);
const PostLaunchOperatingConsolePage = createLazyComponent(
  () => import("@/pages/blog/PostLaunchOperatingConsolePage"),
  undefined,
  "Post Launch Operating Console Blog Post"
);
const PrivacyPreservingCompliancePage = createLazyComponent(
  () => import("@/pages/blog/PrivacyPreservingCompliancePage"),
  undefined,
  "Privacy Preserving Compliance Blog Post"
);
const ProfessionalNetworkingWeb3Page = createLazyComponent(
  () => import("@/pages/blog/ProfessionalNetworkingWeb3Page"),
  undefined,
  "Professional Networking Web3 Blog Post"
);
const ProfileManagementIdentityPage = createLazyComponent(
  () => import("@/pages/blog/ProfileManagementIdentityPage"),
  undefined,
  "Profile Management Identity Blog Post"
);
const PseudonymousVettingPage = createLazyComponent(
  () => import("@/pages/blog/PseudonymousVettingPage"),
  undefined,
  "Pseudonymous Vetting Blog Post"
);
const ReputationPortabilityAcrossChainsPage = createLazyComponent(
  () => import("@/pages/blog/ReputationPortabilityAcrossChainsPage"),
  undefined,
  "Reputation Portability Across Chains Blog Post"
);
const TokenDesignToolsVestingSchedulersPage = createLazyComponent(
  () => import("@/pages/blog/TokenDesignToolsVestingSchedulersPage"),
  undefined,
  "Token Design Tools Vesting Schedulers Blog Post"
);
const VentureOnboardingReadinessVerificationPage = createLazyComponent(
  () => import("@/pages/blog/VentureOnboardingReadinessVerificationPage"),
  undefined,
  "Venture Onboarding Readiness Verification Blog Post"
);
const Web3WalletAuthenticationPage = createLazyComponent(
  () => import("@/pages/blog/Web3WalletAuthenticationPage"),
  undefined,
  "Web3 Wallet Authentication Blog Post"
);
const ZeroKnowledgeKYCPage = createLazyComponent(
  () => import("@/pages/blog/ZeroKnowledgeKYCPage"),
  undefined,
  "Zero Knowledge KYC Blog Post"
);
const ZeroKnowledgeProofsRegulatoryCompliancePage = createLazyComponent(
  () => import("@/pages/blog/ZeroKnowledgeProofsRegulatoryCompliancePage"),
  undefined,
  "Zero Knowledge Proofs Regulatory Compliance Blog Post"
);
const CareersPage = createLazyComponent(
  () => import("@/pages/CareersPage"),
  undefined,
  "Careers Page"
);
const PressKitPage = createLazyComponent(
  () => import("@/pages/PressKitPage"),
  undefined,
  "Press Kit Page"
);
const AcceptableUsePolicyPage = createLazyComponent(
  () => import("@/pages/legal/AcceptableUsePolicyPage"),
  undefined,
  "Acceptable Use Policy Page"
);
const RiskDisclosurePolicyPage = createLazyComponent(
  () => import("@/pages/legal/RiskDisclosurePolicyPage"),
  undefined,
  "Risk Disclosure Policy Page"
);
const DataRoomAccessPolicyPage = createLazyComponent(
  () => import("@/pages/legal/DataRoomAccessPolicyPage"),
  undefined,
  "Data Room Access Policy Page"
);
const AdvertisingPolicyPage = createLazyComponent(
  () => import("@/pages/legal/AdvertisingPolicyPage"),
  undefined,
  "Advertising Policy Page"
);
const CookiePolicyPage = createLazyComponent(
  () => import("@/pages/legal/CookiePolicyPage"),
  undefined,
  "Cookie Policy Page"
);
const RefundAndReturnPolicyPage = createLazyComponent(
  () => import("@/pages/legal/RefundAndReturnPolicyPage"),
  undefined,
  "Refund and Return Policy Page"
);
const DisputeResolutionPolicyPage = createLazyComponent(
  () => import("@/pages/legal/DisputeResolutionPolicyPage"),
  undefined,
  "Dispute Resolution Policy Page"
);
const AccessibilityPolicyPage = createLazyComponent(
  () => import("@/pages/legal/AccessibilityPolicyPage"),
  undefined,
  "Accessibility Policy Page"
);
const DataBreachNotificationPolicyPage = createLazyComponent(
  () => import("@/pages/legal/DataBreachNotificationPolicyPage"),
  undefined,
  "Data Breach Notification Policy Page"
);
const CommunityGuidelinesPage = createLazyComponent(
  () => import("@/pages/legal/CommunityGuidelinesPage"),
  undefined,
  "Community Guidelines Page"
);
const CopyrightDmcaPolicyPage = createLazyComponent(
  () => import("@/pages/legal/CopyrightDmcaPolicyPage"),
  undefined,
  "Copyright DMCA Policy Page"
);
const ContentModerationAppealsPolicyPage = createLazyComponent(
  () => import("@/pages/legal/ContentModerationAppealsPolicyPage"),
  undefined,
  "Content Moderation Appeals Policy Page"
);
const KycAmlSanctionsPolicyPage = createLazyComponent(
  () => import("@/pages/legal/KycAmlSanctionsPolicyPage"),
  undefined,
  "KYC AML Sanctions Policy Page"
);
const SecurityVulnerabilityDisclosurePolicyPage = createLazyComponent(
  () => import("@/pages/legal/SecurityVulnerabilityDisclosurePolicyPage"),
  undefined,
  "Security Vulnerability Disclosure Policy Page"
);
const DaoGovernanceTransparencyPolicyPage = createLazyComponent(
  () => import("@/pages/legal/DaoGovernanceTransparencyPolicyPage"),
  undefined,
  "DAO Governance Transparency Policy Page"
);
const ComplaintsConsumerRightsPolicyPage = createLazyComponent(
  () => import("@/pages/legal/ComplaintsConsumerRightsPolicyPage"),
  undefined,
  "Complaints Consumer Rights Policy Page"
);
const EuUkGdprPrivacyNoticePage = createLazyComponent(
  () => import("@/pages/legal/EuUkGdprPrivacyNoticePage"),
  undefined,
  "EU UK GDPR Privacy Notice Page"
);
const CaliforniaPrivacyNoticePage = createLazyComponent(
  () => import("@/pages/legal/CaliforniaPrivacyNoticePage"),
  undefined,
  "California Privacy Notice Page"
);
const AlphaTokenTermsPage = createLazyComponent(
  () => import("@/pages/legal/AlphaTokenTermsPage"),
  undefined,
  "ALPHA Token Terms Page"
);
const LegalPage = createLazyComponent(
  () => import("@/pages/legal/LegalPage"),
  undefined,
  "Legal Page"
);
const ContactPage = createLazyComponent(
  () => import("@/pages/ContactPage"),
  undefined,
  "Contact Page"
);
const WaitlistPage = createLazyComponent(
  () => import("@/pages/WaitlistPage"),
  undefined,
  "Waitlist Page"
);
const FAQPage = createLazyComponent(
  () => import("@/pages/FAQPage"),
  undefined,
  "FAQ Page"
);
const NotFoundPage = createLazyComponent(
  () => import("@/pages/NotFoundPage"),
  undefined,
  "Not Found Page"
);

function AppContent() {
  useScrollToTop();
  useRoutePreload();
  usePageTracking();

  return (
    <Layout>
      <Routes>
        <Route
          path={ROUTES.HOME}
          element={
            <RouteSuspense>
              <HomePage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.ECOSYSTEM}
          element={
            <RouteSuspense>
              <EcosystemPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.AI_ENGINE}
          element={
            <RouteSuspense>
              <AIEnginePage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.TOKENOMICS}
          element={
            <RouteSuspense>
              <TokenomicsPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.TRACTION}
          element={
            <RouteSuspense>
              <TractionPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.ABOUT_US}
          element={
            <RouteSuspense>
              <AboutUsPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.ELEVATE_TO_ALPHA}
          element={
            <RouteSuspense>
              <ElevateToAlphaPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.OUR_TEAM}
          element={
            <RouteSuspense>
              <OurTeamPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.USER_JOURNEYS}
          element={
            <RouteSuspense>
              <UserJourneysPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.IGAC}
          element={
            <RouteSuspense>
              <IntegrityGatedAmbassadorChannelPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.REGULATORY_COMPLIANCE}
          element={
            <RouteSuspense>
              <RegulatoryCompliancePage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.SOCIAL_IMPACT_COLLECTIVE}
          element={
            <RouteSuspense>
              <SocialImpactCollectivePage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.ROADMAP}
          element={
            <RouteSuspense>
              <RoadmapPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.PRIVACY_POLICY}
          element={
            <RouteSuspense>
              <PrivacyPolicyPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.TERMS_OF_SERVICE}
          element={
            <RouteSuspense>
              <TermsOfServicePage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.DOCUMENTATION}
          element={
            <RouteSuspense>
              <DocumentationPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.WHITEPAPER}
          element={
            <RouteSuspense>
              <WhitepaperPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.TOKEN_ECONOMICS_DOWNLOAD}
          element={
            <RouteSuspense>
              <TokenEconomicsDownloadPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.COMPLIANCE_HUB}
          element={
            <RouteSuspense>
              <ComplianceHubPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.SECURITY}
          element={
            <RouteSuspense>
              <SecurityPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG}
          element={
            <RouteSuspense>
              <BlogPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_LAYERZERO_OFT}
          element={
            <RouteSuspense>
              <LayerZeroOFTExplainedPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_BEYOND_THE_AUDIT_CONTINUOUS_SECURITY_VALIDATION}
          element={
            <RouteSuspense>
              <BeyondTheAuditContinuousSecurityValidationPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_BLOCKCHAIN_ANALYTICS}
          element={
            <RouteSuspense>
              <BlockchainAnalyticsInvestorProtectionPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_BRIDGES_ATTACK_SURFACE}
          element={
            <RouteSuspense>
              <BridgesAttackSurfacePage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_BUG_BOUNTIES}
          element={
            <RouteSuspense>
              <BugBountiesResponsibleDisclosurePage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_COMPLIANCE_FIRST}
          element={
            <RouteSuspense>
              <ComplianceFirstArchitecturePage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_SECURITY_BY_DESIGN}
          element={
            <RouteSuspense>
              <SecurityByDesignNotSloganPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_OFT_VS_WRAPPED}
          element={
            <RouteSuspense>
              <OFTvsWrappedTokensPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_SUPPLY_INTEGRITY}
          element={
            <RouteSuspense>
              <SupplyIntegrityAcrossChainsPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_TOKENOMICS_VERIFY}
          element={
            <RouteSuspense>
              <TokenomicsYouCanVerifyPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_CROSS_CHAIN_IDENTITY}
          element={
            <RouteSuspense>
              <CrossChainIdentityReputationPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_CROSS_CHAIN_BRIDGES_OFT}
          element={
            <RouteSuspense>
              <CrossChainBridgesOFTMessagingPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_INCIDENT_RESPONSE_OMNICHAIN}
          element={
            <RouteSuspense>
              <IncidentResponseOmnichainPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_INSTITUTIONAL_GRADE_SECURITY}
          element={
            <RouteSuspense>
              <InstitutionalGradeSecurityPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_BRIDGE_UX_SECURITY}
          element={
            <RouteSuspense>
              <BridgeUXSecurityChecklistPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_SMART_CONTRACT_AUDITS}
          element={
            <RouteSuspense>
              <SmartContractAuditsExplainedPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_MONITORING_THREAT_DETECTION}
          element={
            <RouteSuspense>
              <MonitoringThreatDetectionPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_SECURE_KEY_MANAGEMENT}
          element={
            <RouteSuspense>
              <SecureKeyManagementPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_TWO_FACTOR_AUTHENTICATION}
          element={
            <RouteSuspense>
              <TwoFactorAuthenticationCryptoPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_END_TO_END_ENCRYPTION}
          element={
            <RouteSuspense>
              <EndToEndEncryptionFintechPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_ENFORCING_ACCOUNTABILITY_POST_LAUNCH_GOVERNANCE_OVERSIGHT}
          element={
            <RouteSuspense>
              <EnforcingAccountabilityPostLaunchGovernanceOversightPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_EMERGENCY_WITHDRAWALS_CIRCUIT_BREAKERS}
          element={
            <RouteSuspense>
              <EmergencyWithdrawalsCircuitBreakersPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_RUG_PULLS_LAUNCH_SCAMS}
          element={
            <RouteSuspense>
              <RugPullsLaunchScamsPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_AI_BLOCKCHAIN_SECURITY}
          element={
            <RouteSuspense>
              <AIBlockchainSecurityPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_ALPHA_AI_ENGINE}
          element={
            <RouteSuspense>
              <AlphaAIEnginePage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_ELEVATE_TO_ALPHA}
          element={
            <RouteSuspense>
              <ElevateToAlphaBlogPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_ALPHA_SHOWCASES}
          element={
            <RouteSuspense>
              <AlphaShowcasesPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_ALPHA_SHORTLISTS}
          element={
            <RouteSuspense>
              <AlphaShortlistsPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_PROOF_OF_EXECUTION}
          element={
            <RouteSuspense>
              <ProofOfExecutionPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_TALENT_MARKETPLACE_ESCROW}
          element={
            <RouteSuspense>
              <TalentMarketplaceEscrowPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_LIQUIDITY_PLANNING_TOOLS}
          element={
            <RouteSuspense>
              <LiquidityPlanningToolsPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_MARKET_MAKER_INVENTORY_UNLOCKS}
          element={
            <RouteSuspense>
              <MarketMakerInventoryUnlocksPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_DEX_LISTING_READINESS}
          element={
            <RouteSuspense>
              <DEXListingReadinessPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_UTILITY_BOUND_TOKENS_101}
          element={
            <RouteSuspense>
              <UtilityBoundTokens101Page />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_DISCLOSURE_DISCIPLINE}
          element={
            <RouteSuspense>
              <DisclosureDisciplinePage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_STAKING_ANALYTICS_DASHBOARD}
          element={
            <RouteSuspense>
              <StakingAnalyticsDashboardPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_MEASURING_UTILITY}
          element={
            <RouteSuspense>
              <MeasuringUtilityPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_LIQUIDITY_CONTROLS_101}
          element={
            <RouteSuspense>
              <LiquidityControls101Page />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_ALLOCATION_BANDS_EXPLAINED}
          element={
            <RouteSuspense>
              <AllocationBandsExplainedPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_TRANSPARENT_ORDER_BOOKS}
          element={
            <RouteSuspense>
              <TransparentOrderBooksPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_UNLOCKS_CLIFFS_LINEAR_VESTING}
          element={
            <RouteSuspense>
              <UnlocksCliffsLinearVestingPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_THIRTY_MONTH_EMISSIONS_PLAN}
          element={
            <RouteSuspense>
              <ThirtyMonthEmissionsPlanPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_TRANSPARENCY_STACK}
          element={
            <RouteSuspense>
              <TransparencyStackPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_GOVERNANCE_APPROVED_PARAMETER_CHANGES}
          element={
            <RouteSuspense>
              <GovernanceApprovedParameterChangesPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_READINESS_GATES_DISCLOSURES}
          element={
            <RouteSuspense>
              <ReadinessGatesDisclosuresPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_VALUE_ACCRUAL_ENGINE}
          element={
            <RouteSuspense>
              <ValueAccrualEnginePage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_BUYBACK_BURN_VS_ALLOCATE}
          element={
            <RouteSuspense>
              <BuybackBurnVsAllocatePage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_SINGLE_SIDED_VS_LP_STAKING}
          element={
            <RouteSuspense>
              <SingleSidedVsLPStakingPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_NON_CASHABLE_REWARDS}
          element={
            <RouteSuspense>
              <NonCashableRewardsPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_AML_CTF_COMPLIANCE}
          element={
            <RouteSuspense>
              <AMLCTFComplianceSafetyPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_GOVERNANCE_RISKS_OMNICHAIN}
          element={
            <RouteSuspense>
              <GovernanceRisksOmnichainPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_GUARDIANS_OF_CROSS_CHAIN_INTEGRITY}
          element={
            <RouteSuspense>
              <GuardiansOfCrossChainIntegrityPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_FAST_BRIDGES_SYSTEMIC_RISK}
          element={
            <RouteSuspense>
              <FastBridgesSystemicRiskPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_FROM_WALL_STREET_TO_WEB3}
          element={
            <RouteSuspense>
              <FromWallStreetToWeb3AdaptingTraditionalRiskControlsPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_REPLAY_ATTACKS_REORG_RISK}
          element={
            <RouteSuspense>
              <ReplayAttacksReorgRiskPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_RESILIENCE_AS_A_SERVICE}
          element={
            <RouteSuspense>
              <ResilienceAsAServicePage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_RUG_PULL_DETECTION}
          element={
            <RouteSuspense>
              <RugPullDetectionPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_MEV_FRONT_RUNNING}
          element={
            <RouteSuspense>
              <MEVFrontRunningPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_CRYPTO_REGULATION_TRENDS_2026}
          element={
            <RouteSuspense>
              <CryptoRegulationTrends2026Page />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_DYNAMIC_COMPLIANCE}
          element={
            <RouteSuspense>
              <DynamicComplianceAdaptingToRegulatoryChangeInACrossChainPlatformPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_CHOOSE_TRUSTED_LAUNCHPAD}
          element={
            <RouteSuspense>
              <HowToChooseTrustedLaunchpadPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_USER_PRIVACY_COMPLIANCE}
          element={
            <RouteSuspense>
              <UserPrivacyVsCompliancePage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_SECURITY_METRICS}
          element={
            <RouteSuspense>
              <SecurityMetricsEveryPlatformPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_SECURITY_CULTURE}
          element={
            <RouteSuspense>
              <SecurityCultureProductFeaturePage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_SIMULATING_THE_LAUNCH}
          element={
            <RouteSuspense>
              <SimulatingTheLaunchHowWeTestAndTuneOperationsBeforeGoLivePage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_STAKING_AND_SECURITY}
          element={
            <RouteSuspense>
              <StakingAndSecurityPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_TRUST_STACK}
          element={
            <RouteSuspense>
              <TheTrustStackCombiningCodeComplianceAndCommunityToSecureWeb3InvestmentsPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_ON_CHAIN_MONITORING}
          element={
            <RouteSuspense>
              <OnChainMonitoring101Page />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_ADMIN_DASHBOARD_AND_ANALYTICS}
          element={
            <RouteSuspense>
              <AdminDashboardAndAnalyticsPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_ANONYMOUS_CROSS_CHAIN_REPUTATION}
          element={
            <RouteSuspense>
              <AnonymousCrossChainReputationPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_ANONYMOUS_STAKING_AND_GOVERNANCE}
          element={
            <RouteSuspense>
              <AnonymousStakingAndGovernancePage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_CAIP_STANDARDS_CROSS_CHAIN_IDENTITY}
          element={
            <RouteSuspense>
              <CAIPStandardsCrossChainIdentityPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_COMPLIANCE_AWARE_LAUNCH_WORKFLOWS}
          element={
            <RouteSuspense>
              <ComplianceAwareLaunchWorkflowsPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_COMPREHENSIVE_AUDIT_LOGGING}
          element={
            <RouteSuspense>
              <ComprehensiveAuditLoggingPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_CROSS_CHAIN_CREDENTIAL_SYSTEMS}
          element={
            <RouteSuspense>
              <CrossChainCredentialSystemsPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_CROSS_CHAIN_VESTING}
          element={
            <RouteSuspense>
              <CrossChainVestingPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_GOVERNANCE_PARTICIPATION_WITHOUT_IDENTITY}
          element={
            <RouteSuspense>
              <GovernanceParticipationWithoutIdentityPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_INFLUENCER_CREATOR_PLATFORM}
          element={
            <RouteSuspense>
              <InfluencerCreatorPlatformPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_LAUNCHPAD_MILESTONE_MANAGEMENT}
          element={
            <RouteSuspense>
              <LaunchpadMilestoneManagementPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_LEGAL_COMPLIANCE_FRAMEWORKS_ANONYMOUS_WEB3}
          element={
            <RouteSuspense>
              <LegalComplianceFrameworksAnonymousWeb3Page />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_MULTI_SITE_ECOSYSTEM_ARCHITECTURE}
          element={
            <RouteSuspense>
              <MultiSiteEcosystemArchitecturePage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_NON_CUSTODIAL_MARKETPLACE_ARCHITECTURE}
          element={
            <RouteSuspense>
              <NonCustodialMarketplaceArchitecturePage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_POST_LAUNCH_OPERATING_CONSOLE}
          element={
            <RouteSuspense>
              <PostLaunchOperatingConsolePage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_PRIVACY_PRESERVING_COMPLIANCE}
          element={
            <RouteSuspense>
              <PrivacyPreservingCompliancePage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_PROFESSIONAL_NETWORKING_WEB3}
          element={
            <RouteSuspense>
              <ProfessionalNetworkingWeb3Page />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_PROFILE_MANAGEMENT_IDENTITY}
          element={
            <RouteSuspense>
              <ProfileManagementIdentityPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_PSEUDONYMOUS_VETTING}
          element={
            <RouteSuspense>
              <PseudonymousVettingPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_REPUTATION_PORTABILITY_ACROSS_CHAINS}
          element={
            <RouteSuspense>
              <ReputationPortabilityAcrossChainsPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_TOKEN_DESIGN_TOOLS_VESTING_SCHEDULERS}
          element={
            <RouteSuspense>
              <TokenDesignToolsVestingSchedulersPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_VENTURE_ONBOARDING_READINESS_VERIFICATION}
          element={
            <RouteSuspense>
              <VentureOnboardingReadinessVerificationPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_WEB3_WALLET_AUTHENTICATION}
          element={
            <RouteSuspense>
              <Web3WalletAuthenticationPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_ZERO_KNOWLEDGE_KYC}
          element={
            <RouteSuspense>
              <ZeroKnowledgeKYCPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.BLOG_ZERO_KNOWLEDGE_PROOFS_REGULATORY_COMPLIANCE}
          element={
            <RouteSuspense>
              <ZeroKnowledgeProofsRegulatoryCompliancePage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.CAREERS}
          element={
            <RouteSuspense>
              <CareersPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.PRESS_KIT}
          element={
            <RouteSuspense>
              <PressKitPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.ACCEPTABLE_USE_POLICY}
          element={
            <RouteSuspense>
              <AcceptableUsePolicyPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.RISK_DISCLOSURE_POLICY}
          element={
            <RouteSuspense>
              <RiskDisclosurePolicyPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.DATA_ROOM_ACCESS_POLICY}
          element={
            <RouteSuspense>
              <DataRoomAccessPolicyPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.ADVERTISING_POLICY}
          element={
            <RouteSuspense>
              <AdvertisingPolicyPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.COOKIE_POLICY}
          element={
            <RouteSuspense>
              <CookiePolicyPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.REFUND_AND_RETURN_POLICY}
          element={
            <RouteSuspense>
              <RefundAndReturnPolicyPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.DISPUTE_RESOLUTION_POLICY}
          element={
            <RouteSuspense>
              <DisputeResolutionPolicyPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.ACCESSIBILITY_POLICY}
          element={
            <RouteSuspense>
              <AccessibilityPolicyPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.DATA_BREACH_NOTIFICATION_POLICY}
          element={
            <RouteSuspense>
              <DataBreachNotificationPolicyPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.COMMUNITY_GUIDELINES}
          element={
            <RouteSuspense>
              <CommunityGuidelinesPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.COPYRIGHT_DMCA_POLICY}
          element={
            <RouteSuspense>
              <CopyrightDmcaPolicyPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.CONTENT_MODERATION_APPEALS_POLICY}
          element={
            <RouteSuspense>
              <ContentModerationAppealsPolicyPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.KYC_AML_SANCTIONS_POLICY}
          element={
            <RouteSuspense>
              <KycAmlSanctionsPolicyPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.SECURITY_VULNERABILITY_DISCLOSURE_POLICY}
          element={
            <RouteSuspense>
              <SecurityVulnerabilityDisclosurePolicyPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.DAO_GOVERNANCE_TRANSPARENCY_POLICY}
          element={
            <RouteSuspense>
              <DaoGovernanceTransparencyPolicyPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.COMPLAINTS_CONSUMER_RIGHTS_POLICY}
          element={
            <RouteSuspense>
              <ComplaintsConsumerRightsPolicyPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.EU_UK_GDPR_PRIVACY_NOTICE}
          element={
            <RouteSuspense>
              <EuUkGdprPrivacyNoticePage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.CALIFORNIA_PRIVACY_NOTICE}
          element={
            <RouteSuspense>
              <CaliforniaPrivacyNoticePage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.ALPHA_TOKEN_TERMS}
          element={
            <RouteSuspense>
              <AlphaTokenTermsPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.LEGAL}
          element={
            <RouteSuspense>
              <LegalPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.CONTACT}
          element={
            <RouteSuspense>
              <ContactPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.WAITLIST}
          element={
            <RouteSuspense>
              <WaitlistPage />
            </RouteSuspense>
          }
        />
        <Route
          path={ROUTES.FAQ}
          element={
            <RouteSuspense>
              <FAQPage />
            </RouteSuspense>
          }
        />
        {/* Legacy URL redirects - redirect old URLs to new URLs */}
        <Route
          path={ROUTES.INTEGRATED_SECURITY_PROTOCOL}
          element={<Navigate to={ROUTES.SECURITY} replace />}
        />
        <Route path={ROUTES.OUR_VISION} element={<Navigate to={ROUTES.HOME} replace />} />
        <Route
          path={ROUTES.RISK_MITIGATION_STRATEGY}
          element={<Navigate to={ROUTES.HOME} replace />}
        />
        <Route
          path={ROUTES.OUR_ECOSYSTEM}
          element={<Navigate to={ROUTES.ECOSYSTEM} replace />}
        />
        <Route
          path={ROUTES.OUR_TOKENOMICS}
          element={<Navigate to={ROUTES.TOKENOMICS} replace />}
        />
        <Route
          path={ROUTES.OUR_TRACTION}
          element={<Navigate to={ROUTES.TRACTION} replace />}
        />
        <Route
          path={ROUTES.OUR_ROADMAP}
          element={<Navigate to={ROUTES.ROADMAP} replace />}
        />
        <Route
          path={ROUTES.WHITE_PAPER}
          element={<Navigate to={ROUTES.WHITEPAPER} replace />}
        />
        <Route
          path="*"
          element={
            <RouteSuspense>
              <NotFoundPage />
            </RouteSuspense>
          }
        />
      </Routes>
    </Layout>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AppContent />
        {features.chatGptFaq && <ChatWidget />}
      </Router>
    </ErrorBoundary>
  );
}
